import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CompanyInvoiceSummary } from '../../../features/manage-payment/payment-history/models/company-invoice-summary';
import { CompanyPaymentDetailsResponse } from '../../../features/manage-payment/payment-history/models/company-payment-details-response';
import { ApiResponse } from '../../models/api-response.model';
import { PaginatedResponse } from '../../models/paginated-response';
import e from 'express';
import { DashboardStats } from '../../models/dashboard-stats';

@Injectable({ providedIn: 'root' })
export class StatisticsService {
  private readonly apiUrl = `${environment.apiGatewayBaseUrl}/Statistics`;
  private readonly apiUrlWalletWithdrawal = `${environment.apiGatewayBaseUrl}`;

  constructor(private http: HttpClient) { }

  private toDateStr(d?: string | Date | null): string {
    if (!d) return '';
    const date = d instanceof Date ? d : new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  /** GET: /Statistics/dashboard */
  getDashboardStats(): Observable<ApiResponse<DashboardStats>> {
    return this.http.get<ApiResponse<DashboardStats>>(`${this.apiUrl}/dashboard`);
  }

  /** GET: /Statistics/cumulative-income/group-by-fee */
  getCumulativeIncomeGroupByFee(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/cumulative-income/group-by-fee`);
  }

  /**
   * GET: /Statistics/cumulative-income/{trnFeeCode}/history?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
   * Pass "ROL_*" to view the entire family.
   */
  getCumulativeIncomeHistory(
    trnFeeCode: string,
    startDate?: string | Date,
    endDate?: string | Date
  ): Observable<ApiResponse<any>> {
    let params = new HttpParams();
    const toDate = (d?: string | Date) =>
      !d ? '' : (d instanceof Date
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        : (d.includes('T') ? d.slice(0, 10) : d));

    const s = toDate(startDate);
    const e = toDate(endDate);
    if (s) params = params.set('startDate', s);
    if (e) params = params.set('endDate', e);

    return this.http.get<ApiResponse<any>>(
      `${this.apiUrl}/cumulative-income/${encodeURIComponent(trnFeeCode)}/history`,
      { params }
    );
  }

  /** GET: /Statistics/eww/companies/totals */
  getCompanyEwwTotals(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/eww/companies/totals`);
  }

  /** GET: /Statistics/eww/companies/{companyId}/salary-cycles/totals */
  getCompanyEwwTotalsPerSalaryCycle(companyId: number): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${this.apiUrl}/eww/companies/${encodeURIComponent(companyId)}/salary-cycles/totals`
    );
  }

  /**
   * GET: /Statistics/eww/companies/{companyId}/salary-cycles/{salaryCycleId}/withdrawals?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
   * Optional inclusive date range.
   */
  getEmployeeEwwsInSalaryCycle(
    companyId: number,
    salaryCycleId: number,
    startDate?: string | Date,
    endDate?: string | Date
  ): Observable<ApiResponse<any>> {
    let params = new HttpParams();
    const toDate = (d?: string | Date) =>
      !d ? '' : (d instanceof Date
        ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        : (d.includes('T') ? d.slice(0, 10) : d));

    const s = toDate(startDate);
    const e = toDate(endDate);
    if (s) params = params.set('startDate', s);
    if (e) params = params.set('endDate', e);

    return this.http.get<ApiResponse<any>>(
      `${this.apiUrl}/eww/companies/${encodeURIComponent(companyId)}/salary-cycles/${encodeURIComponent(salaryCycleId)}/withdrawals`,
      { params }
    );
  }

  /** GET: /Statistics/eww/companies/{companyId}/salary-cycle-instances/totals?numCycles=6 */
  getCompanyEwwRecentCycleInstancesTotals(companyId: number, numCycles: number = 6): Observable<ApiResponse<any>> {
    let params = new HttpParams().set('numCycles', numCycles.toString());
    return this.http.get<ApiResponse<any>>(
      `${this.apiUrl}/eww/companies/${encodeURIComponent(companyId)}/salary-cycle-instances/totals`,
      { params }
    );
  }

  /**
   * GET: /Statistics/eww/companies/{companyId}/salary-cycles/{salaryCycleId}/instances/withdrawals?cycleStart=ISO&cycleEnd=ISO&startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
   * Required: cycleStart and cycleEnd as ISO strings.
   * Optional: inclusive date range within the instance.
   */
  getEmployeeEwwsInSalaryCycleInstance(
    companyId: number,
    salaryCycleId: number,
    cycleStart: string | Date,
    cycleEnd: string | Date,
    startDate?: string | Date,
    endDate?: string | Date
  ): Observable<ApiResponse<any>> {
    let params = new HttpParams();

    // Send all dates in yyyy-MM-dd format
    const cs = this.toDateStr(startDate);
    const ce = this.toDateStr(endDate);
    if (cs) params = params.set('cycleStart', cs);
    if (ce) params = params.set('cycleEnd', ce);

    return this.http.get<ApiResponse<any>>(
      `${this.apiUrl}/eww/companies/${encodeURIComponent(companyId)}/salary-cycles/${encodeURIComponent(salaryCycleId)}/instances/withdrawals`,
      { params }
    );
  }

  // ───────────────────────────────────────────────────────────────
  // Wallet Withdrawals (REAL API ENDPOINTS)
  // ───────────────────────────────────────────────────────────────
  /** GET: /WalletWithdrawal/company/total-wallet-withdrawals/year/:year/month/:month */
  getCompanyWalletWithdrawalTotals(year: number, month: number): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${this.apiUrlWalletWithdrawal}/WalletWithdrawal/company/total-wallet-withdrawals/year/${year}/month/${String(month).padStart(2, '0')}`
    );
  }

  getCompanyWalletWithdrawalTotalsByDateRange(startDate?: string, endDate?: string, year?: number, month?: number, companyId?: number): Observable<ApiResponse<any>> {
    // Build query parameters
    let params = new HttpParams();

    if (companyId !== undefined && companyId !== null) {
      params = params.set('companyId', companyId.toString());
    }

    // Priority: startDate/endDate over year/month
    if (startDate && endDate) {
      params = params.set('startDate', startDate);
      params = params.set('endDate', endDate);
    } else if (year !== undefined && month !== undefined) {
      params = params.set('year', year.toString());
      params = params.set('month', String(month).padStart(2, '0'));
    }

    return this.http.get<ApiResponse<any>>(
      `${this.apiUrlWalletWithdrawal}/WalletWithdrawal/company/total-wallet-withdrawal-details`, { params }
    );
  }

  /** GET: /WalletWithdrawal/company/:companyId/year/:year/month/:month/employee-summary */
  getCompanyEmployeeWalletWithdrawalTotals(
    companyId: number,
    startDate?: string,
    endDate?: string,
    year?: number,
    month?: number,
  ): Observable<ApiResponse<any>> {
    // Build query parameters
    let params = new HttpParams();

    if (companyId !== undefined && companyId !== null) {
      params = params.set('companyId', companyId.toString());
    }

    // Priority: startDate/endDate over year/month
    if (startDate && endDate) {
      params = params.set('startDate', startDate);
      params = params.set('endDate', endDate);
    } else if (year !== undefined && month !== undefined) {
      params = params.set('year', year.toString());
      params = params.set('month', String(month).padStart(2, '0'));
    }

    return this.http.get<ApiResponse<any>>(
      `${this.apiUrlWalletWithdrawal}/WalletWithdrawal/company/${companyId}/employee-total-wallet-summary-by-range`, { params }
    );
  }

  /** GET: /WalletWithdrawal/company/:companyId/employees/:employeeId/withdrawals/year/:year/month/:month */
  getEmployeeWalletWithdrawals(
    companyId: number,
    employeeId: number | string,
    startDate?: string,
    endDate?: string,
    year?: number,
    month?: number,
  ): Observable<ApiResponse<any>> {
    // Build query parameters
    let params = new HttpParams();

    // Priority: startDate/endDate over year/month
    if (startDate && endDate) {
      params = params.set('startDate', startDate);
      params = params.set('endDate', endDate);
    } else if (year !== undefined && month !== undefined) {
      params = params.set('year', year.toString());
      params = params.set('month', String(month).padStart(2, '0'));
    }

    return this.http.get<ApiResponse<any>>(
      `${this.apiUrlWalletWithdrawal}/WalletWithdrawal/company/${companyId}/employee/${employeeId}/daily-withdrawals-by-range`, { params }
    );
  }

  downloadWalletSummaryReport(
    companyId: number | string,
    companyName: string,
    startDate: string,
    endDate: string
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get(
      `${this.apiUrlWalletWithdrawal}/WalletWithdrawal/company/id/${companyId}/name/${encodeURIComponent(companyName)}/withdrawal-summary/download`,
      {
        params,
        responseType: 'blob',
        observe: 'response'
      }
    );
  }

  downloadWageWithdrawalHistoryReport(
    companyId: number | string,
    companyName: string,
    startDate: string,
    endDate: string
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get(
      `${this.apiUrlWalletWithdrawal}/WageWithdrawals/company/id/${companyId}/name/${encodeURIComponent(companyName)}/wage-withdrawal-history/download`,
      {
        params,
        responseType: 'blob',
        observe: 'response'
      }
    );
  }

  downloadWalletWithdrawalTransactionsReport(
    companyId: number | string,
    companyName: string,
    startDate: string,
    endDate: string
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get(
      `${this.apiUrlWalletWithdrawal}/WalletWithdrawal/company/id/${companyId}/name/${encodeURIComponent(companyName)}/withdrawal-transactions/download`,
      {
        params,
        responseType: 'blob',
        observe: 'response'
      }
    );
  }

  // ───────────────────────────────────────────────────────────────//
  // Payment History Statistics (APIs) //
  // ───────────────────────────────────────────────────────────────//
  getCompanyInvoiceSummary(
    monthNumber?: number,
    year?: number,
  ): Observable<ApiResponse<Array<CompanyInvoiceSummary>>> {
    let params = new HttpParams();

    if (monthNumber) {
      params = params.set('monthNumber', monthNumber.toString());
    }
    if (year) {
      params = params.set('year', year.toString());
    }

    return this.http.get<ApiResponse<Array<CompanyInvoiceSummary>>>(
      `${this.apiUrl}/company-invoice-summary`,
      { params }
    );
  }

  getCompanyPaymentDetails(
    companyId: number,
    monthNumber: number,
    year: number
  ): Observable<ApiResponse<CompanyPaymentDetailsResponse>> {
    const params = new HttpParams({
      fromObject: {
        monthNumber: monthNumber.toString(),
        year: year.toString()
      }
    });

    return this.http.get<ApiResponse<CompanyPaymentDetailsResponse>>(
      `${this.apiUrl}/company-payment-details/${companyId}`,
      { params }
    );
  }

  // ───────────────────────────────────────────────────────────────//
  // Paginated (APIs) //
  // ───────────────────────────────────────────────────────────────//
  getCumulativeIncomeGroupByFeePaginated(
    pageNumber?: number,
    pageSize?: number,
    trnFeeCode?: string | null
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    if (pageNumber) params = params.set('pageNumber', pageNumber.toString());
    if (pageSize) params = params.set('pageSize', pageSize.toString());
    if (trnFeeCode) params = params.set('trnFeeCode', trnFeeCode);
    return this.http.get<PaginatedResponse<any>>(
      `${this.apiUrl}/cumulative-income/group-by-fee/paginated`, { params }
    );
  }

  getCumulativeIncomeHistoryPaginated(
    trnFeeCode: string,
    pageNumber?: number,
    pageSize?: number,
    companyId?: string | null,
    startDate?: string | null,
    endDate?: string | null
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    if (pageNumber) params = params.set('pageNumber', pageNumber.toString());
    if (pageSize) params = params.set('pageSize', pageSize.toString());
    if (companyId) params = params.set('companyId', companyId.toString());
    if (startDate) params = params.set('startDate', startDate);
    if (endDate) params = params.set('endDate', endDate);
    return this.http.get<PaginatedResponse<any>>(
      `${this.apiUrl}/cumulative-income/${encodeURIComponent(trnFeeCode)}/history/paginated`,
      { params }
    );
  }

  getCompanyEwwTotalsPaginated(
    pageNumber?: number,
    pageSize?: number,
    companyId?: string | null
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    if (pageNumber) params = params.set('pageNumber', pageNumber.toString());
    if (pageSize) params = params.set('pageSize', pageSize.toString());
    if (companyId) params = params.set('companyId', companyId.toString());
    return this.http.get<PaginatedResponse<any>>(
      `${this.apiUrl}/eww/companies/totals/paginated`, { params }
    );
  }

  getEmployeeEwwsInSalaryCycleInstancePaginated(
    companyId: number,
    salaryCycleId: number,
    cycleStart: string,
    cycleEnd: string,
    pageNumber?: number,
    pageSize?: number,
    employeeId?: string | number| null,
    startDate?: string | Date | null,
    endDate?: string | Date | null
  ): Observable<PaginatedResponse<any>> {
    let params = new HttpParams();
    
    // Send all dates in yyyy-MM-dd format
    const cs = this.toDateStr(startDate);
    const ce = this.toDateStr(endDate);
    if (cs) params = params.set('cycleStart', cs);
    if (ce) params = params.set('cycleEnd', ce);

    if (pageNumber) params = params.set('pageNumber', pageNumber.toString());
    if (pageSize) params = params.set('pageSize', pageSize.toString());
    if (employeeId) params = params.set('employeeId', employeeId);
    
    return this.http.get<PaginatedResponse<any>>(
      `${this.apiUrl}/eww/companies/${companyId}/salary-cycles/${salaryCycleId}/instances/withdrawals/paginated`,
      { params }
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // These call the new "all-companies" endpoints on each microservice.
  // Place them alongside the existing single-company download methods.
  // ─────────────────────────────────────────────────────────────────────────────

  // ── Wallet Summary — All Companies (TransactionMicroService) ──────────────────
  downloadAllCompaniesWalletSummaryReport(
    startDate: string,
    endDate: string
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get(
      `${this.apiUrlWalletWithdrawal}/WalletWithdrawal/all-companies/withdrawal-summary/download`,
      {
        params,
        responseType: 'blob',
        observe: 'response'
      }
    );
  }

  // ── Wallet In (Wage Withdrawal History) — All Companies (RRLMicroService) ─────
  downloadAllCompaniesWageWithdrawalHistoryReport(
    startDate: string,
    endDate: string
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get(
      `${this.apiUrlWalletWithdrawal}/WageWithdrawals/all-companies/wage-withdrawal-history/download`,
      {
        params,
        responseType: 'blob',
        observe: 'response'
      }
    );
  }

  // ── Wallet Out (Withdrawal Transactions) — All Companies (TransactionMicroService)
  downloadAllCompaniesWalletWithdrawalTransactionsReport(
    startDate: string,
    endDate: string
  ): Observable<HttpResponse<Blob>> {
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.http.get(
      `${this.apiUrlWalletWithdrawal}/WalletWithdrawal/all-companies/withdrawal-transactions/download`,
      {
        params,
        responseType: 'blob',
        observe: 'response'
      }
    );
  }

}